import { Edit2, Eye, Plus, Trash2, Upload, UserIcon } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppButton from "../../components/ui/AppButton";
import DashboardFilterBar, {
  ALL_OPTION,
  boolFromToggleFilter,
  isAllFilter,
  STATUS_FILTER_OPTIONS,
  stringFromFilter,
} from "../../components/ui/dashboard/DashboardFilterBar";
import Pagination from "../../components/ui/dashboard/Pagination";
import Table, { Column } from "../../components/ui/dashboard/Table";
import ThreeDotsMenu from "../../components/ui/ThreeDotsMenu";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import { buildApiUrl } from "../../lib/utils";
import DeleteUserModal from "../user/ui/DeleteUserModal";
import { Partner, PartnerSearchRequest, PartnerSearchResponse } from "./@types";
import PartnerBulkUploadModal from "./components/PartnerBulkUploadModal";
import { useGetPartnersMutation } from "./partnerApiSlice";
import PartnerModal from "./PartnerModal";
import PartnerDashStats from "./refactor/PartnerDashStats";

const VEHICLE_COUNT_OPTIONS = [
  ALL_OPTION,
  ...Array.from({ length: 15 }, (_, index) => ({
    value: String(index + 1),
    label: String(index + 1),
  })),
];

const PartnerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [noOfVehicles, setNoOfVehicles] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [openModal, setOpenModal] = useState("");
  const debouncedKeyword = useDebouncedValue(searchTerm.trim());

  const [getPartner, { data: partnerData, isLoading, error: loadError }] =
    useGetPartnersMutation();

  const fetchPartner = useCallback(async () => {
    const requestBody: PartnerSearchRequest = {
      pageNumber: currentPage,
      pageSize,
      keyword: debouncedKeyword || undefined,
      noOfVehicles: stringFromFilter(noOfVehicles)
        ? Number(noOfVehicles)
        : undefined,
      isActive: boolFromToggleFilter(statusFilter, "active", "inactive"),
      orderBy: ["CreatedOn"],
    };

    try {
      await getPartner(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch partners:", err);
    }
  }, [
    currentPage,
    pageSize,
    debouncedKeyword,
    noOfVehicles,
    statusFilter,
    getPartner,
  ]);

  useEffect(() => {
    fetchPartner();
  }, [fetchPartner]);

  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedKeyword, noOfVehicles, statusFilter]);

  // Table columns configuration
  const columns: Column<Partner>[] = [
    {
      key: "userName",
      header: "Partners name",
      width: "",
      render: (value: string, item: Partner) => (
        <div className="flex items-center space-x-3">
          {item.imageUrl ? (
            <img
              src={buildApiUrl(item.imageUrl)}
              alt={`${item.fullName}`}
              className="w-[30px] h-[30px] rounded-full object-cover border border-gray-300 shadow-sm"
            />
          ) : (
            <div className="w-[30px] h-[30px] rounded-full bg-gray-200 flex items-center justify-center border border-gray-300 shadow-sm p-1">
              <UserIcon className="w-full h-full text-hColor" />
            </div>
          )}
          <div className="flex flex-col">
            <span className="text-hColor capitalize">{item.fullName}</span>
          </div>
        </div>
      ),
    },
    {
      key: "payOutId",
      header: "Payout ID",
      width: "24",
      render: (value: string) => (
        <span className="text-hColor capitalize">{value}</span>
      ),
    },
    {
      key: "totalVehicle",
      header: "No of vehicles",
      width: "20",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "phoneNumber",
      header: "Phone number",
      width: "20",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "totalEarning",
      header: "Total earnings (NGN)",
      width: "16",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "isActive",
      header: "Status",
      width: "20",
      render: (value: boolean, item: Partner) => {
        const getStatusInfo = () => {
          if (!value)
            return {
              label: "Inactive",
              color: "text-gray-200 border border-gray-200",
            };
          if (item.isActive)
            return {
              label: "Active",
              color: "text-green-800 border border-green-800",
            };
          return {
            label: "Active",
            color: "text-green-800 border border-green-800",
          };
        };

        const status = getStatusInfo();
        return (
          <span
            className={`w-[60px] inline-flex items-center justify-center px-2 py-1 text-xs font-semibold rounded-full ${status.color}`}
          >
            {status.label}
          </span>
        );
      },
    },
    {
      key: "actions",
      header: "Action",
      width: "12",
      align: "center" as const,
      render: (value: any, item: Partner) => {
        return (
          <ThreeDotsMenu
            actions={[
              {
                label: "View",
                onClick: () => navigate(`/admin/partners/${item?.userId}`),
                icon: <Eye className="w-4 h-4" />,
              },
              {
                label: "Edit",
                onClick: () => {
                  setSelectedPartner(item);
                  setOpenModal("edit");
                },
                icon: <Edit2 className="w-4 h-4" />,
              },
              {
                label: "Delete partner",
                onClick: () => {
                  setSelectedPartner(item);
                  setOpenModal("delete");
                },
                icon: <Trash2 className="w-4 h-4" />,
              },
            ]}
          />
        );
      },
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const handleModal = () => {
    setOpenModal("");
    fetchPartner();
  };

  const activeFilters = useMemo(() => {
    const chips: { key: string; label: string; onRemove: () => void }[] = [];
    if (debouncedKeyword) {
      chips.push({
        key: "keyword",
        label: `Search: ${debouncedKeyword}`,
        onRemove: () => setSearchTerm(""),
      });
    }
    if (!isAllFilter(noOfVehicles)) {
      chips.push({
        key: "vehicles",
        label: `Vehicles: ${noOfVehicles}`,
        onRemove: () => setNoOfVehicles("all"),
      });
    }
    if (!isAllFilter(statusFilter)) {
      chips.push({
        key: "status",
        label: `Status: ${STATUS_FILTER_OPTIONS.find((o) => o.value === statusFilter)?.label}`,
        onRemove: () => setStatusFilter("all"),
      });
    }
    return chips;
  }, [debouncedKeyword, noOfVehicles, statusFilter]);

  const response = partnerData?.data as PartnerSearchResponse | undefined;

  return (
    <div className="min-h-screen py-6 w-full bg-[#F9F9F9] p-4">
      <div className="w-full mx-auto space-y-6">
        {/* Dashboard Cards */}
        <div className="">
          <PartnerDashStats />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-[10px] p-4">
          <div className="">
            <h2 className="text-xl font-semibold text-hColor">All partners</h2>
          </div>

          <div className="flex gap-3">
            <AppButton
              appearance="outline"
              onClick={() => setOpenModal("bulk")}
              className="flex items-center px-4 py-2 rounded-lg w-auto h-[38px]"
            >
              <Upload className="h-4 w-4 mr-2" />
              Bulk upload
            </AppButton>
            <AppButton
              onClick={() => setOpenModal("add")}
              className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/60 transition-colors min-w-[146px] h-[38px]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add partner
            </AppButton>
          </div>
        </div>

        {/* All partners section */}
        <div className="space-y-4 bg-white rounded-[10px] p-4">
          <DashboardFilterBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onClearSearch={() => setSearchTerm("")}
            searchPlaceholder="Search by name, email or phone"
            isLoading={isLoading}
            filters={[
              {
                id: "status",
                placeholder: "Status",
                value: statusFilter,
                options: STATUS_FILTER_OPTIONS,
                onChange: (value) => setStatusFilter(value || "all"),
              },
              {
                id: "vehicles",
                placeholder: "No of vehicles",
                value: noOfVehicles,
                options: VEHICLE_COUNT_OPTIONS,
                onChange: (value) => setNoOfVehicles(value || "all"),
                showSearch: true,
              },
            ]}
            chips={activeFilters}
            onClearAll={() => {
              setSearchTerm("");
              setNoOfVehicles("all");
              setStatusFilter("all");
            }}
            resultCount={response?.totalCount}
            resultLabel={response?.totalCount === 1 ? "partner" : "partners"}
          />
            <Table
              data={response?.data || []}
              columns={columns}
              loading={isLoading}
              error={loadError}
              emptyMessage="No partners found"
            />

            {/* Pagination */}
            {response && (
              <Pagination
                currentPage={response.currentPage}
                totalPages={response.totalPages}
                pageSize={response.pageSize}
                totalCount={response.totalCount}
                hasNextPage={response.hasNextPage}
                hasPreviousPage={response.hasPreviousPage}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            )}
        </div>
      </div>

      <PartnerModal
        isOpen={openModal === "add" || openModal === "edit"}
        onClose={handleModal}
        partner={selectedPartner}
      />

      <PartnerBulkUploadModal
        isOpen={openModal === "bulk"}
        onClose={handleModal}
      />

      <DeleteUserModal
        isOpen={openModal === "delete"}
        onClose={handleModal}
        userId={selectedPartner?.userId}
        entityLabel="partner"
      />
    </div>
  );
};

export default PartnerDashboard;
